import { useEffect } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { TextInput } from 'cpool-components/input/TextInput';
import {
    APPLICANT_ATTRIBUTION_BOTH,
    type APPLICANT_ATTRIBUTION_BOTH_TYPE,
    LOAN_REQUEST_COMMANDS,
} from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { FPS } from '~/config.ts';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { FormSegmentedControl } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormSegmentedControl';
import { FormTextInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormTextInput';
import { useApplicantAttributionOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaPersonal } from '~/features/leadDetails/schemas/personal.schema.ts';
import { canExecuteApplicantCommand, canExecuteForAttribution } from '~/features/leadDetails/utils/aclPermissions';
import { checkIBAN } from '~/features/leadDetails/utils/checkIBAN.ts';
import { externalFetch } from '~/features/leadDetails/utils/externalFetch.ts';

type AccountHolderProps = {
    twoApplicants: boolean;
};

const AccountHolder: React.FC<AccountHolderProps> = ({ twoApplicants }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const { control, getValues, setValue, trigger } = useFormContext<SchemaPersonal>();
    const attributionOptions = useApplicantAttributionOptions();

    useEffect(() => {
        if (twoApplicants) return;
        const accountHolderAttribution = getValues('haushalt.kontoverbindung')?.gehoertZuAntragsteller;
        if (accountHolderAttribution === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1) return;
        setValue('haushalt.kontoverbindung.gehoertZuAntragsteller', APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1);
        if (
            accountHolderAttribution === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2 ||
            accountHolderAttribution === APPLICANT_ATTRIBUTION_BOTH.BEIDE
        ) {
            toast.info(t('PERSONAL_DATA.ACCOUNT_HOLDER_AUTO_CHANGED'), { toastId: 'accountHolderDefaultToKn1' });
        }
    }, [getValues, setValue, t, twoApplicants]);

    const lookUpIBAN = async (iban: string) => {
        if (!iban) {
            return;
        }

        const trimmedIBAN = iban.replace(/ /g, '');

        if (!checkIBAN(trimmedIBAN)) {
            return;
        }

        const response = await externalFetch(
            FPS.API_HOST,
            `/api/iban/check/${trimmedIBAN}`,
            { method: 'GET' },
            FPS.IBAN_TOKEN,
        );
        if (!response) {
            return;
        }
        const ibanDetails = await response.json();
        if (!ibanDetails || ibanDetails.status !== 'ok') {
            return;
        }
        setValue('haushalt.kontoverbindung.bic', ibanDetails.data.bankDetails.bic);
        setValue('haushalt.kontoverbindung.kreditinstitut', ibanDetails.data.bankDetails.name);
        await trigger('haushalt.kontoverbindung.bic');
    };

    const gehoertRaw = getValues('haushalt.kontoverbindung')?.gehoertZuAntragsteller;
    const gehoertForPerm: APPLICANT_ATTRIBUTION_BOTH_TYPE =
        gehoertRaw != null && (Object.values(APPLICANT_ATTRIBUTION_BOTH) as string[]).includes(gehoertRaw)
            ? gehoertRaw
            : APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1;
    const accountHolderDisabled = !canExecuteForAttribution({
        attribution: gehoertForPerm,
        commandName: LOAN_REQUEST_COMMANDS.changeBank,
        permissions,
    });
    const isAttributionDisabled =
        !canExecuteApplicantCommand({
            applicant: 1,
            commandName: LOAN_REQUEST_COMMANDS.changeBank,
            permissions,
        }) ||
        !canExecuteApplicantCommand({
            applicant: 2,
            commandName: LOAN_REQUEST_COMMANDS.changeBank,
            permissions,
        });

    return (
        <FormCard>
            {twoApplicants && (
                <FormSegmentedControl
                    control={control}
                    id="haushalt.kontoverbindung.gehoertZuAntragsteller"
                    label={t('PERSONAL_DATA.ACCOUNT_HOLDER')}
                    options={attributionOptions}
                    disabled={isAttributionDisabled}
                />
            )}
            <FormField<SchemaPersonal, string | null> control={control} name="haushalt.kontoverbindung.iban">
                {({ error, onBlur, onChange, value }) => (
                    <TextInput
                        id="haushalt.kontoverbindung.iban"
                        label={t('PERSONAL_DATA.IBAN')}
                        labelClassName="h-8 w-[175px] shrink-0 justify-start"
                        value={value ?? ''}
                        onBlur={onBlur}
                        onChange={async (event) => {
                            onChange(event.target.value);
                            await lookUpIBAN(event.target.value);
                        }}
                        disabled={accountHolderDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <FormTextInput
                control={control}
                id="haushalt.kontoverbindung.bic"
                label={t('PERSONAL_DATA.BIC')}
                disabled={accountHolderDisabled}
            />
            <FormTextInput
                control={control}
                id="haushalt.kontoverbindung.kreditinstitut"
                label={t('PERSONAL_DATA.BANK')}
                disabled={accountHolderDisabled}
            />
        </FormCard>
    );
};

export default AccountHolder;
